/**
 * `/login`, `/invite/:token` and `/auth/callback` - the screens that render
 * without the shell.
 *
 * V1 section 9 asks for organization-aware login with OTP or SSO, states for an
 * expired invite, a wrong identity and access pending, and a preserved safe
 * return path.
 *
 * WHAT THIS DEPLOYMENT CAN ACTUALLY DO
 *   Signing in through the identity provider is real: `/auth/signin` reads the
 *   provider's discovery document, mints a single-use `state` over an S256
 *   PKCE verifier this process keeps, and `/auth/callback` exchanges the code
 *   with the client secret and verifies the identity token against the
 *   provider's published keys. What it cannot do is produce a session this
 *   application can use, and the screens below say so rather than implying
 *   otherwise. So:
 *
 *   - The sign-in control is live when the deployment names a complete provider
 *     and disabled, with the reason, when it does not.
 *   - The one-time-code control is gone. V1 section 9 asks for "OTP or SSO" and
 *     this provider is SSO; a second button pointing at a path the provider does
 *     not serve is the defect being removed, not a feature to keep alongside it.
 *   - The callback screen reports the provider's verdict. A verified provider
 *     identity is NOT a session: `apps/api` verifies HS256 tokens of its own,
 *     and an Authentik token is RS256 with another issuer, another audience and
 *     no organization. `src/oidc.ts` sets out the whole of that.
 *   - A session token issued elsewhere can still be supplied directly. That is
 *     the honest way to drive the application against the real API, and it is
 *     labelled as what it is.
 *   - The invite screen calls the real acceptance route, so an invited person can
 *     accept.
 *
 * WHAT THE REFUSALS LOOK LIKE, WHICH IS A DECISION AND NOT AN OMISSION
 *   This file's header used to say the expired, wrong-identity and not-a-member
 *   outcomes "are rendered verbatim". They are not, and they must not be:
 *   `app.accept_invite` reports a token that was already used with the SAME
 *   `invite_not_found` as a token that never existed, and a token that has
 *   expired with `invite_expired` — whose detail carries the expiry instant.
 *   `apps/api` already collapses that to the single sentence `Not found.`
 *   (errors.ts, CANONICAL_MESSAGE), which V1 §4 rule 5 and ACL-01 require: hidden
 *   and missing must be indistinguishable, and a page that names the expiry
 *   instant is an existence oracle with a human reader.
 *
 *   So the explanation below is UNCONDITIONAL. It is on the screen before
 *   anything is attempted and it does not change when an attempt is refused,
 *   because a sentence that varies with the outcome would tell the reader which
 *   outcome occurred. Explaining expiry and resend without discriminating is
 *   possible and is what AUTH-01's "expiry and resend explained" can mean while
 *   ACL-01 holds; explaining WHICH of the two it was, here or anywhere else,
 *   cannot.
 *
 *   There is no resend control on this screen and there cannot be one: the only
 *   way to mint an invitation is `app.create_invite`, called from the
 *   administrator's panel (0041), and an invitee has no route to it. A button
 *   pointing at a path that does not exist is the defect this file already
 *   removed once, when the one-time-code control was deleted for being one.
 */

import type { AppShell } from '../app.js';
import { type RawHtml, html } from '../lib/html.js';
import type { Route } from '../lib/router.js';
import { safeReturnPath } from '../lib/router.js';
import { docHead } from '../ui/components.js';

export function renderGate(app: AppShell, route: Route): RawHtml {
  switch (route.name) {
    case 'login':
      return loginScreen(app);
    case 'landing':
      return landingScreen(app);
    case 'invite':
      return inviteScreen(app, route.token);
    case 'callback':
      return callbackScreen(app);
    default:
      // Not "No such page". This branch is reached when the caller has no
      // session and the route is not one of the three that work signed-out —
      // so the page is not missing, the reader simply is not signed in, and
      // saying otherwise contradicts the sentence directly beneath it.
      //
      // It used to claim "No such page" here AND "Sign in to continue" below,
      // which is two incompatible explanations in one screen. Worse, it wore the
      // same words as the real not-found at `app.ts:451`, so an upstream that
      // was misconfigured, a token that was refused, and a URL that genuinely
      // did not exist were indistinguishable on screen. That cost hours today:
      // three unrelated defects read as one routing bug, and the routing fix was
      // correct the whole time.
      return html`
        <div class="gate">
          <div class="gate-inner">
            ${docHead([{ label: 'Section', value: 'Sign in' }], 'Sign in to continue')}
            <p class="lede">This screen is available once you are signed in to an organization.</p>
            <a class="btn btn-secondary" href="/login">Go to sign in</a>
          </div>
        </div>
      `;
  }
}

function loginScreen(app: AppShell): RawHtml {
  const params = new URLSearchParams(app.state.search);
  const returnTo = safeReturnPath(params.get('return'), '/login');
  const organization = params.get('org') ?? 'not selected';
  const signIn = app.client.config.signIn;
  const session = app.state.session;

  return html`
    <div class="gate">
      <div class="gate-inner">
        ${docHead(
          [
            { label: 'Organization', value: organization },
            { label: 'Session', value: session === null ? 'none' : 'active' },
          ],
          'Sign in to Skills Manager',
          'Skills Manager is organization-scoped. Your identity provider establishes who you are; your membership in the selected organization decides what you can reach.',
        )}

        <div class="panel">
          <div class="panel-head"><h2>Identity</h2></div>
          <div class="panel-body stack-sm">
            ${signInControl(signIn.available, returnTo)}
            ${
              signIn.available
                ? html`<p class="cell-note">
                    This sends you to the identity provider and comes back here. It establishes who
                    they are with them; it does not by itself open a session in Skills Manager — see the
                    panel below for what does.
                  </p>`
                : html`<div class="callout">
                    <span class="callout-tag">Unavailable</span>
                    <p>
                      ${signIn.reason ?? 'This deployment names no identity provider.'} Sign in with
                      the identity provider is therefore disabled rather than broken. Set
                      <code>OIDC_ISSUER</code>, <code>OIDC_CLIENT_ID</code>,
                      <code>OIDC_CLIENT_SECRET</code> and <code>OIDC_REDIRECT_URI</code>, then
                      restart the web process.
                    </p>
                  </div>`
            }
          </div>
        </div>

        <div class="panel">
          <div class="panel-head"><h2>Existing session token</h2></div>
          <div class="panel-body stack-sm">
            <p class="cell-note">
              <code>apps/api</code> publishes no credential-issuing endpoint: it verifies an HS256
              token of its own and refuses everything else, so there is nothing in this build that
              can mint the session a sign-in would need. Until one exists, a session token issued
              elsewhere can be supplied here directly. It is held in this browser tab only and is
              cleared when the tab closes.
            </p>
            <label class="field-label" for="session-token">Session token</label>
            <textarea
              class="field"
              id="session-token"
              rows="4"
              autocomplete="off"
              spellcheck="false"
              placeholder="eyJ..."
            ></textarea>
            <div class="btn-row">
              <button class="btn btn-primary" data-action="gate:sign-in">Use this token</button>
              ${session === null ? '' : html`<button class="btn btn-secondary" data-action="sign-out">Sign out</button>`}
            </div>
            <div aria-live="polite">
              ${
                app.state.notice === null
                  ? ''
                  : html`<p class="cell-note">${app.state.notice.text}</p>`
              }
            </div>
          </div>
        </div>
      </div>
    </div>
  `;
}

/**
 * `/`, for a reader with no session. THE SIGN-IN IS THE PAGE.
 *
 * The user asked for single sign-on on the main page, and there were three ways
 * to do it. This is the one that keeps the page:
 *
 *   - a `302` from `/` to `/auth/signin` makes the landing page unreachable and
 *     bounces an ALREADY-SIGNED-IN person back out to the provider on every
 *     visit, which `app.ts` avoids by sending them to their own library instead;
 *   - rendering the sign-in directly is what this is, reached through the
 *     client router rather than through a redirect, so the address bar keeps
 *     saying `/` and no navigation history is spent on it.
 *
 * ONE PRIMARY CONTROL, AND IT IS THE SSO ONE. The remaining sign-in routes —
 * including the field that admits a session token minted elsewhere, which is
 * how this build is driven against a real API without a credential-issuing
 * endpoint — sit below it as secondary. They stay reachable: they are the
 * honest way to drive the application, not decoration.
 *
 * The return path is `/`, so a completed sign-in comes back here, adopts the
 * session cookie and is forwarded to the organization rather than parking on a
 * signed-in landing page.
 */
function landingScreen(app: AppShell): RawHtml {
  const signIn = app.client.config.signIn;

  return html`
    <div class="gate">
      <div class="gate-inner">
        ${docHead(
          [{ label: 'Section', value: 'Sign in' }],
          'Skills Manager',
          'The organization skill library. Sign in with your identity provider to continue.',
        )}
        <div class="panel">
          <div class="panel-head"><h2>Sign in</h2></div>
          <div class="panel-body stack-sm">
            ${signInControl(signIn.available, '/', 'Sign in with Skills Manager')}
            ${
              signIn.available
                ? html`<p class="cell-note">
                    This sends you to the identity provider and brings you back here. It
                    establishes who you are with them.
                  </p>`
                : html`<div class="callout">
                    <span class="callout-tag">Unavailable</span>
                    <p>
                      ${signIn.reason ?? 'This deployment names no identity provider.'} The control
                      above is therefore disabled rather than broken.
                    </p>
                  </div>`
            }
            <div class="btn-row">
              <a class="btn btn-secondary" href="/login">Other sign-in options</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  `;
}

/**
 * The sign-in control, or the same control disabled.
 *
 * WHY THE LINK IS THIS ORIGIN AND NOT THE PROVIDER'S
 *   The authorization request must carry a `state` this process generated, a
 *   `code_challenge` that is the S256 hash of a `code_verifier` this process
 *   keeps and never sends, and a client id from configuration. A page that
 *   rendered the provider's URL itself would have had to generate the first two
 *   in the browser, which is the arrangement in which `state` is a value the
 *   client chose and the verifier never existed at all. So the link is
 *   `/auth/signin`, which answers `302` to the provider's own
 *   `authorization_endpoint` read from its discovery document.
 *
 * It is still a real anchor rather than a click handler, which is what lets it
 * open in a new tab, be copied, and be read by anything that reads links.
 */
function signInControl(available: boolean, returnTo: string, label = 'Continue with single sign-on'): RawHtml {
  if (!available) {
    return html`<button class="btn btn-primary" disabled>${label}</button>`;
  }
  const target = `/auth/signin?return=${encodeURIComponent(returnTo)}`;
  return html`<a class="btn btn-primary" href="${target}" data-full-page>${label}</a>`;
}

function inviteScreen(app: AppShell, token: string): RawHtml {
  return html`
    <div class="gate">
      <div class="gate-inner">
        ${docHead([{ label: 'Section', value: 'Invitation' }], 'Accept an invitation')}
        <div class="panel">
          <div class="panel-head"><h2>Invitation</h2></div>
          <div class="panel-body stack-sm">
            <p>
              Accepting creates your membership in the organization that issued this invitation. It
              requires a verified email address, and the address on your account must match the one the
              invitation was sent to.
            </p>
            <dl class="dl">
              <dt>Token</dt>
              <dd><code>${token}</code></dd>
            </dl>
            <div class="btn-row">
              <button class="btn btn-primary" data-action="gate:accept-invite">Accept invitation</button>
              <a class="btn btn-secondary" href="/login">Sign in first</a>
            </div>
            <div aria-live="assertive">
              ${
                app.state.notice === null
                  ? ''
                  : html`<div class="${app.state.notice.tone === 'bad' ? 'callout callout-danger' : 'callout'}">
                    <span class="callout-tag">${app.state.notice.tone === 'bad' ? 'Refused' : 'Accepted'}</span>
                    <p>${app.state.notice.text}</p>
                  </div>`
              }
            </div>
            <div class="callout">
              <span class="callout-tag">If it does not work</span>
              <p>
                An invitation works once. This link is refused the same way whether it has already
                been used, has expired, or never existed — deliberately, so that nothing on this
                page reveals which addresses were ever invited. It will not work again: a second
                attempt with the same link is refused rather than ignored. Only an organization
                administrator can send another, it is a new link, and this one is never
                reactivated.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  `;
}

/**
 * What the sign-in produced — which, for a successful one, is nothing at all.
 *
 * A completed sign-in no longer lands here. `apps/web/src/server.ts` hands the
 * verified assertion to `apps/api`, gets a session, and redirects straight to
 * the page the person was trying to reach with the token in a cookie. So
 * every outcome rendered below is a REFUSAL, and this screen's job is to say
 * which one in the person's terms.
 *
 * That is a change from the screen this replaced, which rendered "No session"
 * on the success path. It did so accurately — the build genuinely established
 * nothing — and `docs/V1.md:109` and `docs/V1.md:345` both require a login
 * that establishes something, and `SSO-01` requires an access-pending state
 * that had no screen at all. Both now have one.
 *
 * `access_pending` and `sso_mfa_required` get their own words because they
 * are different facts with different next steps, and collapsing them into
 * "sign-in failed" is what makes a person retry something that will never
 * work: one needs an administrator, the other needs a different sign-in.
 */
const REFUSAL_WORDS: Readonly<Record<string, { readonly tag: string; readonly says: string }>> = {
  access_pending: {
    tag: 'Access pending',
    says:
      'Your identity provider confirmed who you are, and this account has no active membership in ' +
      'that organization. An administrator has to invite you before anything here will open.',
  },
  sso_mfa_required: {
    tag: 'MFA required',
    says:
      'This organization requires a sign-in that proves a second factor, and the identity token your ' +
      'provider issued does not carry one. Sign in again with multi-factor authentication switched on.',
  },
  sso_provider_mismatch: {
    tag: 'Wrong provider',
    says:
      'You signed in with an identity provider this organization has not configured. Sign in with the ' +
      'provider your administrator set up for it.',
  },
  sign_in_expired: {
    tag: 'Sign-in expired',
    says: 'That took longer than this sign-in is kept for. Start again from the sign-in screen.',
  },
  api_unreachable: {
    tag: 'Service unavailable',
    says: 'This application could not reach the service that establishes sessions. Try again shortly.',
  },
};

function callbackScreen(app: AppShell): RawHtml {
  const params = new URLSearchParams(app.state.search);
  const code = params.get('error');
  const described = params.get('error_description');
  const returnTo = safeReturnPath(params.get('return'), '/login');

  const known = code === null ? undefined : REFUSAL_WORDS[code];
  const body = known?.says ?? described ?? 'The sign-in did not complete.';

  return html`
    <div class="gate">
      <div class="gate-inner">
        ${docHead([{ label: 'Section', value: 'Callback' }], 'Signing you in')}
        <div class="panel">
          <div class="panel-body stack-sm">
            ${
              code !== null
                ? html`<div class="callout callout-danger">
                  <span class="callout-tag">${known?.tag ?? 'Refused'}</span>
                  <p>${body}</p>
                </div>`
                : html`<div class="callout">
                  <span class="callout-tag">Nothing to complete</span>
                  <p>
                    This page has no sign-in result to show. It is where the identity provider sends
                    you back to, so arriving here without a result means you followed the link
                    yourself rather than coming back from a provider.
                  </p>
                </div>`
            }
            <div class="btn-row">
              <a class="btn btn-primary" href="/auth/signin?return=${encodeURIComponent(returnTo)}"
                >Sign in again</a
              >
              <a class="btn btn-secondary" href="/login">Back to sign in</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  `;
}
